import { api, apiUrl } from './client'
import { getTokenStore } from '@/lib/authStorage'

export interface SessionState {
  authenticated: boolean
  email?: string
  /** Google given name. Missing for sessions signed before it was stored. */
  name?: string
  /** Epoch ms when this device signed in. */
  signedInAt?: number
}

/** First name for display: Google's given name, else guessed from the email ("karl.noelle@…" → "Karl"). */
export function displayName(session: SessionState): string | undefined {
  if (session.name) return session.name
  const local = session.email?.split('@')[0]?.split(/[._+-]/)[0]
  return local ? local[0]!.toUpperCase() + local.slice(1) : undefined
}

export function fetchSession(): Promise<SessionState> {
  return api.get('/api/auth/session')
}

/** Where the web app sends the browser to start Google sign-in. */
export function googleSignInUrl(): string {
  return apiUrl('/api/auth/google/start')
}

/** Native: swap the one-time code from the sign-in redirect (plus our PKCE verifier) for a session token. */
export async function exchangeNativeCode(code: string, verifier: string): Promise<void> {
  const { token } = await api.post<{ token: string }>('/api/auth/native/exchange', { code, verifier })
  await getTokenStore()?.set(token)
}

export async function logout(): Promise<void> {
  await getTokenStore()?.clear()
  await api.post('/api/auth/logout')
}

const AUTH_ERRORS: Record<string, string> = {
  not_allowed: "That Google account isn't allowed to use Plumb.",
  cancelled: 'Sign-in was cancelled.',
  expired: 'Sign-in took too long. Try again.',
  google: "Google sign-in didn't complete. Try again.",
}

export function describeAuthError(code: string): string {
  return AUTH_ERRORS[code] ?? AUTH_ERRORS['google']!
}
